import { createContext, useContext, useRef, useState } from "react";
import { useKeyDown } from "../utils/customHooks";
import { getLetterOrNumberFromCode, keyCodes } from "../utils/KeyCodes";
import {
  BreadTransaction,
  getNullFieldValueCriterionEquivalent,
  matchTransactionToRule,
} from "breadcommon";
import { UserContext } from "../firebaseio/UserContext";
import {
  firestoreAddRuleAndUpdateTransactions,
  firestoreUpdateTransaction,
} from "../firebaseio/firestoreIo";
import { FirestoreDocRule } from "breadcommon";
import { RulesContext } from "../firebaseio/RulesContext";
import dayjs from "dayjs";
import { buildClasses } from "../utils/buildClasses";
import { TransactionColumnValueCategory } from "./TransactionColumnValueCategory";
import { TransactionColumnValueReviewed } from "./TransactionColumnValueReviewed";
import { TransactionColumnValueRule } from "./TransactionColumnValueRule";
import { TransactionsNavContext } from "./TransactionsScreen";
import { TransactionSelectionContext } from "./TransactionSelectionContext";
import { TransactionColumn } from "./TransactionColumn";
import { TransactionColumnSpacer } from "./TransactionColumnSpacer";
import { TransactionColumnValueDate } from "./TransactionColumnValueDate";
import { TransactionColumnValueAccount } from "./TransactionColumnValueAccount";
import { TransactionColumnValueType } from "./TransactionColumnValueType";
import { TransactionColumnValueMerchant } from "./TransactionColumnValueMerchant";
import { TransactionColumnValueDescription } from "./TransactionColumnValueDescription";
import { TransactionColumnValueAmount } from "./TransactionColumnValueAmount";
import { TransactionColumnValueTags } from "./TransactionColumnValueTags";

export const TransactionRowContext = createContext<{
  transactionSelected: boolean;
  ruleForFieldHighlighting: {
    get: FirestoreDocRule | null;
    set: React.Dispatch<React.SetStateAction<FirestoreDocRule | null>>;
  };
  keyListenerShouldListen: boolean;
}>({
  transactionSelected: false,
  ruleForFieldHighlighting: { get: null, set: () => {} },
  keyListenerShouldListen: false,
});

export function TransactionRow(props: {
  transaction: BreadTransaction;
  rowIndex?: number;
}): JSX.Element {
  const {
    selectedTransactionIds,
    cursorTransactionId,
    selectSingle,
    toggleSelect,
    selectRange,
    deselectAll,
    setCursorTransactionId,
    focusTransactionAtIndex,
    transactionCount,
    selectionHandledRef,
  } = useContext(TransactionSelectionContext);
  const transactionSelected = selectedTransactionIds.has(props.transaction.id);
  const isCursor = cursorTransactionId === props.transaction.id;

  const [ruleForFieldHighlighting, setRuleForFieldHighlighting] =
    useState<FirestoreDocRule | null>(null);
  const [isRuleEditorOpen, setIsRuleEditorOpen] = useState<boolean>(false);
  const [isTagsDropDownOpen, setIsTagsDropDownOpen] = useState<boolean>(false);

  const [transactionsNav] = useContext(TransactionsNavContext);
  const { columnSettings } = transactionsNav;
  const TransactionRowContainerRef = useRef<HTMLDivElement>(null);
  const categoryDropDownRef = useRef<HTMLInputElement | null>(null);
  const user = useContext(UserContext);
  const rules = useContext(RulesContext);

  const keyListenerShouldListen =
    isCursor && !isRuleEditorOpen && !isTagsDropDownOpen;

  useKeyDown(
    TransactionRowContainerRef,
    keyListenerShouldListen,
    handleKeyDown,
  );

  const { bestMatchRule, bestMatchRuleIsActive } = matchTransactionToRule(
    props.transaction,
    rules,
  );

  let newRuleOrderPosition = 0;
  rules.forEach(
    (rule) =>
      (newRuleOrderPosition = Math.min(
        rule.order_position - 1,
        newRuleOrderPosition,
      )),
  );
  function createRuleFromTransactionWithoutId(
    categoryIdAction: string,
  ): Omit<FirestoreDocRule, "id"> {
    return {
      criteria: {
        account_id: null,
        merchant_name:
          props.transaction.merchant?.name ??
          (props.transaction.type !== null
            ? null
            : getNullFieldValueCriterionEquivalent()),
        description:
          props.transaction.merchant || props.transaction.type
            ? null
            : (props.transaction.description ??
              getNullFieldValueCriterionEquivalent()), // only include description if there is no merchant
        partial_description: null,
        type:
          props.transaction.type ??
          (props.transaction.merchant !== null
            ? null
            : getNullFieldValueCriterionEquivalent()),
        subtype:
          props.transaction.subtype ??
          (props.transaction.merchant !== null
            ? null
            : getNullFieldValueCriterionEquivalent()),
      },
      action: {
        category_id: categoryIdAction,
      },
      created_timestamp_secs: dayjs().unix(),
      order_position: newRuleOrderPosition,
    };
  }

  function markReviewed(): void {
    firestoreUpdateTransaction(
      user.uid,
      props.transaction.id,
      {
        manual_data: { reviewed: true },
      },
      null,
    );
  }

  function selectRowAbove() {
    if (props.rowIndex === undefined || props.rowIndex <= 0) return;
    selectionHandledRef.current = true;
    selectSingle(props.rowIndex - 1);
    focusTransactionAtIndex(props.rowIndex - 1);
  }

  function selectRowBelow() {
    if (props.rowIndex === undefined || props.rowIndex >= transactionCount - 1)
      return;
    selectionHandledRef.current = true;
    selectSingle(props.rowIndex + 1);
    focusTransactionAtIndex(props.rowIndex + 1);
  }

  function extendSelectionUp() {
    if (props.rowIndex === undefined || props.rowIndex <= 0) return;
    selectionHandledRef.current = true;
    selectRange(props.rowIndex - 1);
    focusTransactionAtIndex(props.rowIndex - 1);
  }

  function extendSelectionDown() {
    if (props.rowIndex === undefined || props.rowIndex >= transactionCount - 1)
      return;
    selectionHandledRef.current = true;
    selectRange(props.rowIndex + 1);
    focusTransactionAtIndex(props.rowIndex + 1);
  }

  function createNewRule(categoryIdAction: string): void {
    if (!bestMatchRuleIsActive) {
      firestoreAddRuleAndUpdateTransactions(
        user.uid,
        createRuleFromTransactionWithoutId(categoryIdAction),
      );
    }
  }

  function handleMouseDown(event: React.MouseEvent) {
    selectionHandledRef.current = true;
    if (event.shiftKey) {
      event.preventDefault(); // Prevent text selection
      selectRange(props.rowIndex!);
      TransactionRowContainerRef.current?.focus();
    } else if (event.metaKey || event.ctrlKey) {
      toggleSelect(props.rowIndex!);
    } else {
      selectSingle(props.rowIndex!);
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;

    switch (event.code) {
      case keyCodes.ENTER:
        if (event.repeat) return;
        if (props.transaction.categoryId) {
          markReviewed();
        }
        if (event.shiftKey) {
          selectRowAbove();
        } else {
          selectRowBelow();
        }
        return;
      case keyCodes.UPARROW:
        event.preventDefault();
        if (event.shiftKey) {
          extendSelectionUp();
        } else {
          selectRowAbove();
        }
        break;
      case keyCodes.DOWNARROW:
        event.preventDefault();
        if (event.shiftKey) {
          extendSelectionDown();
        } else {
          selectRowBelow();
        }
        break;
      case keyCodes.ESC:
        event.preventDefault();
        if (event.repeat) return;
        deselectAll();
        TransactionRowContainerRef.current?.blur();
        break;
      default:
        if (
          !(event.ctrlKey || event.metaKey) &&
          getLetterOrNumberFromCode(event.code) !== null
        ) {
          // Don't redirect focus to this row's category dropdown when the
          // user is typing in another input (e.g. the bulk edit bar's
          // dropdowns or the search bar). Otherwise, the typing steals
          // focus away from the input and triggers this row's category
          // dropdown to open.
          const target = event.target as HTMLElement | null;
          if (
            target?.tagName === "INPUT" ||
            target?.tagName === "TEXTAREA" ||
            target?.isContentEditable
          ) {
            break;
          }
          categoryDropDownRef.current?.focus();
        }
        break;
    }
  }

  return (
    <TransactionRowContext.Provider
      value={{
        transactionSelected: transactionSelected,
        ruleForFieldHighlighting: {
          get: ruleForFieldHighlighting,
          set: setRuleForFieldHighlighting,
        },
        keyListenerShouldListen: keyListenerShouldListen,
      }}
    >
      <div
        ref={TransactionRowContainerRef}
        // onClick={() => setTransactionSelected(true)}
        className={buildClasses(
          {
            if: transactionSelected,
            then: buildClasses("bg-purple-100"),
            else: buildClasses("bg-surface", "hover:bg-surface-300"),
          },
          "h-10",
          "flex",
          "items-center",
          "justify-center",
          "py-1",
          "border-t-tiny",
          "border-t-surface-600",
          "first:border-t-0",
          "outline-none",
          "group/txrow",
        )}
        onMouseDown={handleMouseDown}
        onFocus={() => {
          setCursorTransactionId(props.transaction.id);
          if (!selectionHandledRef.current) {
            // Non-mouse focus (tab, programmatic) — single select
            selectSingle(props.rowIndex!);
          }
          selectionHandledRef.current = false;
        }}
        tabIndex={0}
        data-transaction-row={props.rowIndex?.toString() ?? ""}
      >
        {columnSettings[TransactionColumn.DATE].shown && (
          <>
            <TransactionColumnValueDate date={props.transaction.date} />
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.ACCOUNT].shown && (
          <>
            <TransactionColumnValueAccount
              accountId={props.transaction.account_id}
            />
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.TYPE].shown && (
          <>
            <TransactionColumnValueType
              type={props.transaction.type}
              subtype={props.transaction.subtype}
            />
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.MERCHANT].shown && (
          <>
            <TransactionColumnValueMerchant
              merchant={props.transaction.merchant}
            />
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.DESCRIPTION].shown && (
          <>
            <TransactionColumnValueDescription
              description={props.transaction.description}
            />
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.CATEGORY].shown && (
          <>
            <TransactionColumnValueCategory
              transactionId={props.transaction.id}
              categoryId={props.transaction.categoryId}
              createNewRule={createNewRule}
              focusTransaction={() =>
                TransactionRowContainerRef.current?.focus()
              }
              selectPreviousTransaction={selectRowAbove}
              selectNextTransaction={selectRowBelow}
              saveDropDownRef={(inputRef) => {
                categoryDropDownRef.current = inputRef.current;
              }}
            ></TransactionColumnValueCategory>
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.TAGS].shown && (
          <>
            <TransactionColumnValueTags
              transactionId={props.transaction.id}
              tagIds={props.transaction.tagIds}
              onOpenChange={setIsTagsDropDownOpen}
              focusTransaction={() =>
                TransactionRowContainerRef.current?.focus()
              }
            />
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.RULE].shown && (
          <>
            <TransactionColumnValueRule
              transaction={props.transaction}
              activeRule={bestMatchRuleIsActive ? bestMatchRule : null}
              createRuleFromTransactionWithoutId={
                createRuleFromTransactionWithoutId
              }
              ruleEditorIsOpen={isRuleEditorOpen}
              openRuleEditor={() => setIsRuleEditorOpen(true)}
              closeRuleEditor={() => setIsRuleEditorOpen(false)}
              focusTransaction={() =>
                TransactionRowContainerRef.current?.focus()
              }
            />
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.REVIEWED].shown && (
          <>
            <TransactionColumnValueReviewed
              transaction={props.transaction}
            ></TransactionColumnValueReviewed>
            <TransactionColumnSpacer />
          </>
        )}
        {columnSettings[TransactionColumn.AMOUNT].shown && (
          <TransactionColumnValueAmount
            amount={props.transaction.amount}
            currencyCode={props.transaction.currency_code}
          />
        )}
      </div>
    </TransactionRowContext.Provider>
  );
}
